
import { useContext, createContext } from 'react';
import axios, {AxiosResponse} from 'axios';
import AxiosService from './AxiosService';
import { IdToken } from '@auth0/auth0-react';

import { ConfigInfoService } from './ConfigInfo';
import { getApiErrorMessage } from './apiErrorMessage';

import { 
  AdminActivityRecord,
  User_WithoutId, 
  
  OfferWithScope_UserWithoutId,
  ReferralDeleteResponse,
  ReferralConversationResponse,
  ReferralConversationViewRole,
  ReferralCreationRequest,
  ReferralWithScope_UserWithoutId,
  OfferCreationAttributes_FromUser,
  ReferralCreationAttributes_FromUser,
    } 
  from './DataStructures';

export interface AdminActivityFilters {
  kind?: 'request' | 'domain' | 'all';
  eventType?: string;
}


export class APIAuthenticatedService {
  private static instance: APIAuthenticatedService;
  private API_PATH; 
  public axiosService: AxiosService;

  private constructor(getAccessTokenSilently: () => Promise<string>, getIdTokenClaims: () => Promise<IdToken | undefined>, configInfo: ConfigInfoService) {

    this.axiosService = AxiosService.getInstance(getAccessTokenSilently, getIdTokenClaims);      
    
    this.API_PATH = configInfo.apiServer;
  }

  public static getInstance = (getAccessTokenSilently: () => Promise<string>, getIdTokenClaims: () => Promise<IdToken | undefined>, configInfo: ConfigInfoService) => {
    if (!APIAuthenticatedService.instance) {
      APIAuthenticatedService.instance = new APIAuthenticatedService(getAccessTokenSilently, getIdTokenClaims, configInfo);
    }

    return APIAuthenticatedService.instance;
  }

  public init = async () => {

    await this.axiosService.init();
  }

  
  public sendMail = async (text: string, email: string, name: string) : Promise<{message:string}> => {
    try {
      const response = await axios.post<{ message: string }>(
        `${this.API_PATH}/sendmail`,
        { name, email, text },
        { timeout: 2000 }
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(getApiErrorMessage(error));
    }
  }

  public getAuthenticatedUser = async (): Promise<User_WithoutId | null> => {
    try { 

      const email = this.axiosService.getEmail();

      if(!email)
        throw new Error("Can't get email address...");
      
      const response: AxiosResponse<User_WithoutId,any> = await this.axiosService.use.get<User_WithoutId>(`${this.API_PATH}/users/linkToAuth/${email}`);

      return response.data;

    }  
    catch (error: any) {
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }    
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  }

  public getAllUsers = async (): Promise<User_WithoutId[]> => {
    const response = await this.axiosService.use.get<User_WithoutId[]>(`${this.API_PATH}/users`);
    return response.data;
  };

  public getApiInfo= async (): Promise<{message:string}> => {
    const response = await this.axiosService.use.get<{message:string}>(`${this.API_PATH}/info`);
    return response.data;
  };

  public registerPushDevice = async (
    deviceToken: string,
    platform: 'android' | 'ios' = 'android'
  ): Promise<void> => {
    await this.axiosService.use.post(
      `${this.API_PATH}/push-devices/authenticated/register`,
      { deviceToken, platform }
    );
  };

  public deregisterPushDevice = async (deviceToken: string): Promise<void> => {
    await this.axiosService.use.post(
      `${this.API_PATH}/push-devices/authenticated/deregister`,
      { deviceToken }
    );
  };

  // public addAuthenticatedUser = async (userToCreate_withoutEmail: UserCreationAttributes_withoutemail): Promise<User_WithoutId> => {

  //   try {      
      
  //     const email = this.axiosService.getEmail();

  //     if(!email)
  //       throw new Error("Can't get email address...");

  //     const userToCreate: UserCreationAttributes = { email, ...userToCreate_withoutEmail };
  
  //     const response = await this.axiosService.use.post<User_WithoutId, AxiosResponse<User_WithoutId>, UserCreationAttributes>(`${this.API_PATH}/users`, userToCreate);
  //     return response.data;

  //   } 
  //   catch (error: any) {
      
  //     if (error instanceof Error) {
  //       throw new Error(error.message);
  //     } else {
  //       throw new Error("Unknown error");
  //     }
  //   }
  // };

  // public forceAuthenticatedUser = async (userToCreate: UserForcedCreationAttributes): Promise<User_WithoutId> => {

  //   try {  
  //     const response = await this.axiosService.use.post<User_WithoutId, AxiosResponse<User_WithoutId>, UserForcedCreationAttributes>(`${this.API_PATH}/users/force`, userToCreate);
  //     return response.data;

  //   } 
  //   catch (error: any) {
      
  //     if (error instanceof Error) {
  //       throw new Error(error.message);
  //     } else {
  //       throw new Error("Unknown error");
  //     }
  //   }
  // };

  public updateUserAgreementStatus = async (): Promise<User_WithoutId | null> => {
    try {
      const response = await this.axiosService.use.post<{ message: string, user: User_WithoutId }>(`${this.API_PATH}/users/authenticated/agreeTerms`);
      return response.data.user;

    } 
    catch (error: any) {
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }    
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  }

  public claimBalance = async (): Promise<User_WithoutId | null> => {
    try {
      const response = await this.axiosService.use.post<{ message: string, user: User_WithoutId }>(`${this.API_PATH}/users/authenticated/claimBalance`);
      return response.data.user;
    } 
    catch (error: any) {
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }    
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  }

  public getOfferById = async (offerId: number): Promise<OfferWithScope_UserWithoutId | null> => {
    try {
      const response: AxiosResponse<OfferWithScope_UserWithoutId,any> = await this.axiosService.use.get<OfferWithScope_UserWithoutId>(`${this.API_PATH}/offers/${offerId}`);
      return response.data;

    } 
    catch (error: any) {
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }    
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  }

  public getOffersForAuthenticatedUser = async (): Promise<OfferWithScope_UserWithoutId | null> => {

    try {
      const response = await this.axiosService.use.get<OfferWithScope_UserWithoutId | null>(`${this.API_PATH}/offers/user/authenticated`);
      return response.data;

    } 
    catch (error: any) {
      
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }    
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }    
  };

  public getOffersByProfession = async (profession: string): Promise<OfferWithScope_UserWithoutId[]> => {
    const response = await this.axiosService.use.get<OfferWithScope_UserWithoutId[]>(`${this.API_PATH}/offers/profession/${profession}`);
    return response.data;
  };

  public createOffer = async (offerToCreate: OfferCreationAttributes_FromUser): Promise<OfferWithScope_UserWithoutId> => {
    try {
      
      const response = await this.axiosService.use.post<OfferWithScope_UserWithoutId, AxiosResponse<OfferWithScope_UserWithoutId>, OfferCreationAttributes_FromUser>(`${this.API_PATH}/offers`, offerToCreate);
      
      return response.data;
    } 
    catch (error: any) {
      
      throw new Error(getApiErrorMessage(error));
    }
  };

  public withdrawOfferForLoggedInUser = async (): Promise<null> => {
    try {
      
      const response = await this.axiosService.use.delete(`${this.API_PATH}/offers/invalidate/authenticated`);
      
      return response.data;

    }     
    catch (error: any) {
      
      throw new Error(getApiErrorMessage(error));
    }

  };

  public createReferral = async (referralToCreate: ReferralCreationAttributes_FromUser): Promise<void> => {
    try {
      const { customerConsent, ...referralRequest } = referralToCreate;
      void customerConsent;

      // REQ-051: referral creation payloads contain customer data, so this path must not debug-log them.
      await this.axiosService.use.post<
        ReferralWithScope_UserWithoutId,
        AxiosResponse<ReferralWithScope_UserWithoutId>,
        ReferralCreationRequest
      >(
        `${this.API_PATH}/referrals`,
        // REQ-018: keep consent enforcement in the UI while sending only the backend-supported referral fields.
        referralRequest
      );
    
    }     
    catch (error: any) {
      throw new Error(getApiErrorMessage(error));
    }
  };


  public getUserReferralsOut = async (): Promise<ReferralWithScope_UserWithoutId[]> => {
    try {

      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId[]>(`${this.API_PATH}/referrals/referrer/authenticated`);
    
      return response.data;

    }     
    catch (error: any) {
      
      throw new Error(getApiErrorMessage(error));
    }

  };

  public getReferralById = async (referralId: number): Promise<ReferralWithScope_UserWithoutId | null> => {
    try {
      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId>(
        `${this.API_PATH}/referrals/${referralId}`
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public getReferralConversation = async (
    referralId: number
  ): Promise<ReferralConversationResponse> => {
    try {
      const response = await this.axiosService.use.get<ReferralConversationResponse>(
        `${this.API_PATH}/referrals/${referralId}/conversation`
      );

      return response.data;
    }
    catch (error: any) {
      throw new Error(getApiErrorMessage(error));
    }
  };

  public addReferralConversationMessage = async (
    referralId: number,
    message: string
  ): Promise<ReferralWithScope_UserWithoutId> => {
    try {
      const response = await this.axiosService.use.post<
        ReferralWithScope_UserWithoutId,
        AxiosResponse<ReferralWithScope_UserWithoutId>,
        { message: string }
      >(`${this.API_PATH}/referrals/${referralId}/message`, { message });

      return response.data;
    }
    catch (error: any) {
      throw new Error(getApiErrorMessage(error));
    }
  };

  public getUserReferralsIn = async (): Promise<ReferralWithScope_UserWithoutId[]> => {
    try {

      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId[]>(`${this.API_PATH}/referrals/offerer/authenticated`);
    
      return response.data;

    }     
    catch (error: any) {
      
      throw new Error(getApiErrorMessage(error));
    }
  };

  public getAdminGroupOneReferrals = async (): Promise<ReferralWithScope_UserWithoutId[]> => {
    try {
      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId[]>(
        `${this.API_PATH}/referrals/admin/group-1`
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('Authenticated user not found.');
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public getAdminGroupTwoReferrals = async (): Promise<ReferralWithScope_UserWithoutId[]> => {
    try {
      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId[]>(
        `${this.API_PATH}/referrals/admin/group-2`
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('Authenticated user not found.');
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public getAdminReferralById = async (
    referralId: number
  ): Promise<ReferralWithScope_UserWithoutId | null> => {
    try {
      const response = await this.axiosService.use.get<ReferralWithScope_UserWithoutId>(
        `${this.API_PATH}/referrals/admin/${referralId}`
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public getAdminReferralConversation = async (
    referralId: number,
    viewAs: ReferralConversationViewRole
  ): Promise<ReferralConversationResponse> => {
    try {
      const response = await this.axiosService.use.get<ReferralConversationResponse>(
        `${this.API_PATH}/referrals/admin/${referralId}/conversation`,
        {
          params: { viewAs },
        }
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public deleteAdminReferral = async (referralId: number): Promise<ReferralDeleteResponse> => {
    try {
      const response = await this.axiosService.use.delete<ReferralDeleteResponse>(
        `${this.API_PATH}/referrals/admin/${referralId}`,
        {
          // REQ-028: backend requires explicit confirmation for hard-delete admin referral calls.
          params: { confirm: true },
        }
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        throw new Error('Delete confirmation required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('Referral not found.');
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public getAdminActivity = async (
    groupId: string,
    filters?: AdminActivityFilters
  ): Promise<AdminActivityRecord[]> => {
    try {
      const response = await this.axiosService.use.get<AdminActivityRecord[]>(
        `${this.API_PATH}/activity/admin`,
        {
          // REQ-029: hidden admin activity pages filter activity by tenant group.
          params: { groupId, ...filters },
        }
      );

      return response.data;
    }
    catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Error('Admin access required.');
      }
      else if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('Authenticated user not found.');
      }
      else {
        throw new Error(getApiErrorMessage(error));
      }
    }
  };

  public setUserReferralsInStatus = async (
    referralId: number,
    newStatus: string,
    message?: string
  ): Promise<ReferralWithScope_UserWithoutId> => {
    try {
      const response = await this.axiosService.use.put<
        ReferralWithScope_UserWithoutId,
        AxiosResponse<ReferralWithScope_UserWithoutId>,
        { message: string } | undefined
      >(
        `${this.API_PATH}/referrals/${referralId}/status/${newStatus}`,
        message ? { message } : undefined
      );
    
      return response.data;

    }     
    catch (error: any) {
      
      throw new Error(getApiErrorMessage(error));
    }
  };

  // public setUserReferralsPayoutRequested= async (): Promise<ReferralWithScope_UserWithoutId[]> => {
  //   try {

  //     const response = await this.axiosService.use.put<ReferralWithScope_UserWithoutId[]>(`${this.API_PATH}/referrals/referrer/authenticated/payoutRequested`);
      
  //     return response.data;

  //   }     
  //   catch (error: any) {
      
  //     if (error instanceof Error) {
  //       throw new Error(error.message);
  //     } else {
  //       throw new Error("Unknown error");
  //     }
  //   }
  // };

}


export const APIAuthenticatedServiceContext = createContext<APIAuthenticatedService| null>(null);

export function useAPIAuthenticatedService() {      

    const context = useContext(APIAuthenticatedServiceContext);

    if (context === null) {
      throw new Error('useAPIAuthenticatedService must be used within a APIAuthenticatedServiceContext.Provider');
    }
    return context;
  }
